'use client'

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    MdVerified,
    MdCancel,
    MdSecurity,
    MdPerson,
    MdBusiness,
    MdCalendarToday,
    MdDownload,
    MdOpenInNew,
} from 'react-icons/md'
import {
    verifySignatureByAccessCode,
    verifySignaturePublic,
    PublicSignatureVerification,
} from '@/lib/api/medical/signature-api'

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
    PRESCRIPTION: 'Receita',
    CERTIFICATE: 'Atestado',
    MEDICAL_CERTIFICATE: 'Atestado',
    MEDICAL_REPORT: 'Laudo',
    EXAM_ORDER: 'Solicitação de exames',
    EXAM_RESULTS: 'Resultado de exames',
    EVOLUTION: 'Evolução',
    MEDICAL_EVOLUTION: 'Evolução',
    REFERRAL: 'Encaminhamento',
    CONSULTATION: 'Registro de atendimento',
    PROCEDURE: 'Pedido',
}

const ITI_VALIDATOR_URL = 'https://validar.iti.gov.br'

export default function VerifySignaturePage() {
    const { signatureId } = useParams<{ signatureId: string }>()
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState<PublicSignatureVerification | null>(
        null
    )
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (signatureId) {
            loadVerification()
        }
    }, [signatureId])

    const loadVerification = async () => {
        if (!signatureId) return

        setLoading(true)
        setError(null)
        try {
            // O QR code e o link dos documentos levam o código de verificação;
            // links antigos levavam o id numérico da assinatura.
            const response = /^\d+$/.test(signatureId)
                ? await verifySignaturePublic(parseInt(signatureId, 10))
                : await verifySignatureByAccessCode(signatureId)
            if (response.success && response.result) {
                setData(response.result)
            } else {
                setError(
                    response.error || 'Assinatura não encontrada'
                )
            }
        } catch (err) {
            setError('Não foi possível verificar a assinatura')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-base-200 to-base-300">
            {/* Header */}
            <div className="bg-base-100 border-b border-base-300 shadow-sm">
                <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
                    <MdSecurity className="text-2xl text-secondary" />
                    <div>
                        <h1 className="text-lg font-bold text-base-content">
                            Verificação de Assinatura Digital
                        </h1>
                        <p className="text-xs text-base-content/60">
                            Verifique a autenticidade de documentos
                            assinados digitalmente
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 py-8">
                {loading ? (
                    <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                        <div className="loading loading-spinner loading-lg text-secondary"></div>
                        <p className="text-base-content/60 text-sm">
                            Verificando assinatura...
                        </p>
                    </div>
                ) : error ? (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-lg p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
                            <MdCancel className="text-3xl text-error" />
                        </div>
                        <h2 className="text-xl font-bold text-error mb-2">
                            Verificação falhou
                        </h2>
                        <p className="text-base-content/60">{error}</p>
                    </div>
                ) : data ? (
                    <div className="space-y-6">
                        {/* Status da Verificação */}
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                            <div
                                className={`px-6 py-4 ${data.valid ? 'bg-success/10' : 'bg-error/10'}`}
                            >
                                <div className="flex items-center gap-3">
                                    {data.valid ? (
                                        <MdVerified className="text-3xl text-success" />
                                    ) : (
                                        <MdCancel className="text-3xl text-error" />
                                    )}
                                    <div>
                                        <h2
                                            className={`text-xl font-bold ${data.valid ? 'text-success' : 'text-error'}`}
                                        >
                                            {data.valid
                                                ? 'Assinatura Válida'
                                                : 'Assinatura Inválida'}
                                        </h2>
                                        <p className="text-sm text-base-content/60">
                                            {data.valid
                                                ? 'Este documento possui uma assinatura digital válida.'
                                                : 'Não foi possível validar esta assinatura.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {data.documentUrl ? (
                                <div className="px-6 py-5 space-y-4">
                                    <a
                                        href={data.documentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-secondary w-full sm:w-auto gap-2"
                                    >
                                        <MdDownload className="text-lg" />
                                        Baixar documento assinado
                                    </a>
                                    <p className="text-sm text-base-content/60 leading-relaxed">
                                        Para conferir a assinatura ICP-Brasil,
                                        envie o PDF baixado ao validador oficial
                                        do ITI.{' '}
                                        <a
                                            href={ITI_VALIDATOR_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="link link-secondary inline-flex items-center gap-1"
                                        >
                                            validar.iti.gov.br
                                            <MdOpenInNew className="text-xs" />
                                        </a>
                                    </p>
                                </div>
                            ) : null}
                        </div>

                        {/* Dados do Documento */}
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                            <div className="px-6 py-3 border-b border-base-300/50 bg-base-200/50">
                                <h3 className="font-bold text-base-content">
                                    Dados do Documento
                                </h3>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-base-content/50">
                                            Tipo
                                        </p>
                                        <p className="font-medium">
                                            {DOCUMENT_TYPE_LABELS[
                                                data.documentType
                                            ] || data.documentType}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-base-content/50">
                                            {data.accessCode
                                                ? 'Código de verificação'
                                                : 'ID'}
                                        </p>
                                        <p className="font-medium font-mono">
                                            {data.accessCode ||
                                                data.documentId}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-sm">
                                    <p className="text-base-content/50">
                                        Algoritmo de hash
                                    </p>
                                    <p className="font-medium font-mono">
                                        {data.hashAlgorithm}
                                    </p>
                                </div>
                                <div className="text-sm">
                                    <p className="text-base-content/50">
                                        Hash do documento
                                    </p>
                                    <p className="font-medium font-mono text-xs break-all bg-base-200 rounded p-2">
                                        {data.documentHash}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Profissional */}
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                            <div className="px-6 py-3 border-b border-base-300/50 bg-base-200/50">
                                <div className="flex items-center gap-2">
                                    <MdPerson className="text-lg text-secondary" />
                                    <h3 className="font-bold text-base-content">
                                        Profissional Responsável
                                    </h3>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-base-content/50">
                                            Nome
                                        </p>
                                        <p className="font-medium">
                                            {data.professional.name}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-base-content/50">
                                            Registro
                                        </p>
                                        <p className="font-medium">
                                            {data.professional.councilName &&
                                                `${data.professional.councilName} `}
                                            {
                                                data.professional
                                                    .registrationNumber
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Empresa */}
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                            <div className="px-6 py-3 border-b border-base-300/50 bg-base-200/50">
                                <div className="flex items-center gap-2">
                                    <MdBusiness className="text-lg text-secondary" />
                                    <h3 className="font-bold text-base-content">
                                        Estabelecimento
                                    </h3>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-base-content/50">
                                            Razão Social
                                        </p>
                                        <p className="font-medium">
                                            {data.company.name}
                                        </p>
                                    </div>
                                    {data.company.tradingName && (
                                        <div>
                                            <p className="text-base-content/50">
                                                Nome Fantasia
                                            </p>
                                            <p className="font-medium">
                                                {data.company.tradingName}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Certificado */}
                        {data.certificate && (
                            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                                <div className="px-6 py-3 border-b border-base-300/50 bg-base-200/50">
                                    <div className="flex items-center gap-2">
                                        <MdSecurity className="text-lg text-secondary" />
                                        <h3 className="font-bold text-base-content">
                                            Certificado Digital
                                        </h3>
                                    </div>
                                </div>
                                <div className="p-6 space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-base-content/50">
                                                Tipo
                                            </p>
                                            <p className="font-medium">
                                                {data.certificate.type}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-base-content/50">
                                                Emissor
                                            </p>
                                            <p className="font-medium">
                                                {data.certificate.issuer}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-base-content/50">
                                                Número de série
                                            </p>
                                            <p className="font-medium font-mono text-xs">
                                                {
                                                    data.certificate
                                                        .serialNumber
                                                }
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-base-content/50">
                                                Validade
                                            </p>
                                            <p className="font-medium">
                                                {new Date(
                                                    data.certificate.validFrom
                                                ).toLocaleDateString(
                                                    'pt-BR'
                                                )}{' '}
                                                a{' '}
                                                {new Date(
                                                    data.certificate.validUntil
                                                ).toLocaleDateString(
                                                    'pt-BR'
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {data.certificate.crm && (
                                        <div className="text-sm">
                                            <p className="text-base-content/50">
                                                CRM
                                            </p>
                                            <p className="font-medium">
                                                CRM/
                                                {data.certificate.crmState}{' '}
                                                {data.certificate.crm}
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {data.certificate.expired && (
                                            <span className="badge badge-warning gap-1">
                                                Certificado expirado
                                            </span>
                                        )}
                                        {data.certificate.revoked && (
                                            <span className="badge badge-error gap-1">
                                                Certificado revogado
                                            </span>
                                        )}
                                        {!data.certificate.expired &&
                                            !data.certificate.revoked && (
                                                <span className="badge badge-success gap-1">
                                                    Certificado válido
                                                </span>
                                            )}
                                    </div>

                                    {data.certificate
                                        .fingerprintSha256 && (
                                        <div className="text-xs text-base-content/40 break-all mt-2">
                                            SHA-256:{' '}
                                            {
                                                data.certificate
                                                    .fingerprintSha256
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Data da assinatura */}
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-lg p-6">
                            <div className="flex items-center gap-3 text-sm">
                                <MdCalendarToday className="text-lg text-base-content/50" />
                                <div>
                                    <p className="text-base-content/50">
                                        Data da assinatura
                                    </p>
                                    <p className="font-medium">
                                        {new Date(
                                            data.signedAt
                                        ).toLocaleString('pt-BR')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}
